import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * FlyToCart custom element for animating product images to cart.
 *
 * On mobile, the header is forced visible before the cart
 * destination is measured so the animation always flies towards
 * the visible cart icon.
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();

    /*
     * The mobile header may currently be hidden after scrolling.
     * Reveal it before measuring the cart icon position.
     */
    this.#showMobileHeader();

    /*
     * Allow the header visibility classes and transforms to finish
     * updating before IntersectionObserver measures the elements.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#observeAnimationPoints();
      });
    });
  }

  /**
   * Forces the mobile header into its visible state.
   * Desktop behaviour is left completely untouched.
   */
  #showMobileHeader = () => {
    const isMobile = window.matchMedia(
      '(max-width: 749px)'
    ).matches;

    if (!isMobile) return;

    const headerComponent = document.querySelector(
      '#header-component'
    );

    const headerSection = headerComponent?.closest(
      '.header-section'
    );

    const headerTopRow =
      headerComponent?.querySelector('.header__row--top') ||
      document.querySelector('.header__row--top');

    if (!headerSection) return;

    headerSection.classList.remove(
      'loopi-mobile-header-hidden'
    );

    headerSection.classList.add(
      'loopi-mobile-header-visible'
    );

    /*
     * Preserve the compact scrolled styling when the customer
     * has already moved down the page.
     */
    headerTopRow?.classList.toggle(
      'is-scrolled',
      window.scrollY > 10
    );
  };

  /**
   * Measures the product image and cart destination once both
   * elements have been rendered in their correct positions.
   */
  #observeAnimationPoints = () => {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        /** @type {DOMRectReadOnly | null} */
        let sourceRect = null;

        /** @type {DOMRectReadOnly | null} */
        let destinationRect = null;

        entries.forEach((entry) => {
          if (entry.target === this.source) {
            sourceRect = entry.boundingClientRect;
          } else if (entry.target === this.destination) {
            destinationRect = entry.boundingClientRect;
          }
        });

        if (sourceRect && destinationRect) {
          this.#animate(sourceRect, destinationRect);
          intersectionObserver.disconnect();
        }
      }
    );

    intersectionObserver.observe(this.source);
    intersectionObserver.observe(this.destination);
  };

  /**
   * Animates the flying item towards the cart icon.
   *
   * @param {DOMRectReadOnly} sourceRect
   * @param {DOMRectReadOnly} destinationRect
   */
  #animate = async (sourceRect, destinationRect) => {
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2,
      y: sourceRect.top + sourceRect.height / 2,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2,
      y: destinationRect.top + destinationRect.height / 2,
    };

    if (this.useSourceSize) {
      this.style.setProperty(
        '--width',
        `${sourceRect.width}px`
      );

      this.style.setProperty(
        '--height',
        `${sourceRect.height}px`
      );
    }

    this.style.setProperty(
      '--start-x',
      `${startPoint.x}px`
    );

    this.style.setProperty(
      '--start-y',
      `${startPoint.y}px`
    );

    this.style.setProperty(
      '--travel-x',
      `${endPoint.x - startPoint.x}px`
    );

    this.style.setProperty(
      '--travel-y',
      `${endPoint.y - startPoint.y}px`
    );

    await yieldToMainThread();

    await Promise.allSettled(
      this.getAnimations().map(
        (animation) => animation.finished
      )
    );

    this.remove();
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
